<script setup lang="ts">
import {nextTick, ref, useTemplateRef, watch} from "vue";
import {teamPlanningApi} from "@/container";
import {ShiftGrid, ShiftSuggestion, ShiftSuggestionMessage} from "@/core/http/resources/TeamPlanningResource";
import {apiErrorStatus, isAuthorizationError} from "@/core/http/apiErrorMessage";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Input} from "@/core/components/ui/input";
import ScheduleAiAnswer from "@/team/components/ScheduleAiAnswer.vue";
import ScheduleAiEcho from "@/team/components/ScheduleAiEcho.vue";
import {CircleAlert, LoaderCircle, RotateCcw, Sparkles, Undo2} from "@lucide/vue";

const EXAMPLES = [
    '9h–19h du mardi au vendredi, samedi matin',
    '8h30–12h30 et 14h–19h30 du lundi au vendredi',
    'Temps partiel : lundi, mardi, jeudi de 9h à 17h',
];

/** Mirror of the API bound: ten exchanges, older ones are dropped. */
const MAX_MESSAGES = 20;

/** Mirror of the API bound on a replayed answer. */
const MAX_ANSWER_LENGTH = 2000;

/**
 * What the pharmacist can actually do about each failure. A 422 is the model
 * failing twice: the builder cannot send an invalid grid or thread, so the
 * request's own validation never surfaces here.
 */
const ERRORS: Record<number, string> = {
    422: 'Je n\'ai pas réussi à établir une proposition valable. Précisez votre demande ou réessayez.',
    429: 'Trop de demandes en peu de temps : patientez une minute avant de réessayer.',
    503: 'Le service est momentanément indisponible. Réessayez dans quelques instants.',
};

type Entry =
    | {id: number; kind: 'user'; content: string}
    | {id: number; kind: 'answer'; answer: ShiftSuggestion}
    | {id: number; kind: 'error'; message: string};

const {memberId, organizationId, grid} = defineProps<{
    memberId: number;
    organizationId: number;
    /** The grid as it stands in the builder, sent along with every turn. */
    grid: ShiftGrid;
    /** Whether the parent still holds the grid from before the thread. */
    canUndo: boolean;
}>();

const emits = defineEmits<{
    (event: 'proposal', proposal: ShiftGrid): void;
    (event: 'undo'): void;
}>();

const entries = ref<Entry[]>([]);
const draft = ref<string>('');
const generating = ref<boolean>(false);
const thread = useTemplateRef<HTMLElement>('thread');

let nextId = 0;

/**
 * Bumped whenever the thread is discarded: an answer to a thread that no
 * longer exists — another member's, or one the pharmacist restarted — must
 * never land in the current one.
 */
let generation = 0;

/**
 * Forget the thread. Never touches the grid: undoing a proposal is the undo
 * button's job, and restarting a conversation must not do it behind the
 * pharmacist's back.
 */
const reset = () => {
    generation++;
    entries.value = [];
    draft.value = '';
    generating.value = false;
};

/**
 * The panel is not remounted between collaborators, so it clears itself: a
 * thread written for one member must never be one message away from being
 * applied to the next.
 */
watch(() => memberId, reset);

watch([() => entries.value.length, generating], async () => {
    await nextTick();
    thread.value?.scrollTo({top: thread.value.scrollHeight, behavior: 'smooth'});
});

/**
 * The thread as the model replays it. An answer is replayed as text only —
 * the grid it proposed travels as the current grid, which may have been
 * edited by hand since.
 */
const messages = (): ShiftSuggestionMessage[] =>
    entries.value
        .flatMap((entry): ShiftSuggestionMessage[] => {
            if (entry.kind === 'user') {
                return [{role: 'user', content: entry.content}];
            }

            if (entry.kind === 'answer') {
                const content = [entry.answer.reply, ...entry.answer.questions.map(question => question.text)].join('\n');

                return [{role: 'assistant', content: content.slice(0, MAX_ANSWER_LENGTH)}];
            }

            return [];
        })
        .slice(-MAX_MESSAGES);

const request = async () => {
    const asked = generation;

    generating.value = true;

    try {
        const answer = await teamPlanningApi().suggestShifts(organizationId, memberId, messages(), grid);

        if (asked !== generation) {
            return;
        }

        entries.value.push({id: nextId++, kind: 'answer', answer});

        if (answer.proposal) {
            emits('proposal', answer.proposal);
        }
    } catch (error) {
        if (asked === generation && !isAuthorizationError(error)) {
            entries.value.push({
                id: nextId++,
                kind: 'error',
                message: ERRORS[apiErrorStatus(error) ?? 0] ?? 'La demande n\'a pas abouti. Réessayez.',
            });
        }
    } finally {
        // Never clear a spinner that now belongs to another thread's request.
        if (asked === generation) {
            generating.value = false;
        }
    }
};

const send = (text: string) => {
    const content = text.trim();

    if (!content || generating.value) {
        return;
    }

    entries.value.push({id: nextId++, kind: 'user', content});
    draft.value = '';
    request();
};

/** Ask the same thing again, in place of the failure. */
const retry = () => {
    if (generating.value) {
        return;
    }

    if (entries.value.at(-1)?.kind === 'error') {
        entries.value.pop();
    }

    request();
};

/**
 * Undoing the fill goes back to the start, thread included: the model replays
 * the whole thread on every turn, and would rebuild on the restored grid what
 * the pharmacist just undid. Resetting first also drops an answer still on its
 * way, so it never lands after the undo.
 */
const undo = () => {
    reset();
    emits('undo');
};

const isLast = (entry: Entry) => entry === entries.value.at(-1);

defineExpose({reset});
</script>

<template>
  <div class="rounded-lg border border-primary/20 bg-primary/5 p-3">
    <div class="flex flex-wrap items-center gap-2">
      <div class="flex flex-1 items-center gap-1.5 text-xs font-semibold text-primary">
        <Sparkles class="size-3.5" />
        Remplissage par l'IA
      </div>
      <button
        v-if="canUndo"
        class="flex h-7 items-center gap-1.5 rounded-lg border border-solid border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-gray-600 hover:border-gray-300"
        @click="undo"
      >
        <Undo2 class="size-3.5" />
        Annuler le remplissage
      </button>
      <button
        v-if="entries.length"
        class="flex h-7 items-center gap-1.5 rounded-lg border border-solid border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-gray-600 hover:border-gray-300"
        title="Vider la conversation, sans toucher à la grille"
        @click="reset"
      >
        <RotateCcw class="size-3.5" />
        Recommencer
      </button>
    </div>

    <div
      v-if="entries.length"
      ref="thread"
      class="mt-2.5 flex max-h-80 flex-col gap-2 overflow-y-auto pr-1"
    >
      <template v-for="entry in entries" :key="entry.id">
        <ScheduleAiEcho v-if="entry.kind === 'user'" :content="entry.content" />
        <ScheduleAiAnswer
          v-else-if="entry.kind === 'answer'"
          :answer="entry.answer"
          :interactive="isLast(entry) && !generating"
          @reply="send"
        />
        <div
          v-else
          class="flex max-w-[90%] flex-col items-start gap-1.5 self-start rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          <span class="flex items-start gap-1.5">
            <CircleAlert class="mt-px size-3.5 shrink-0" />
            {{ entry.message }}
          </span>
          <button
            v-if="isLast(entry)"
            class="h-6 rounded-full border border-solid border-red-200 bg-white px-2.5 text-[11px] font-semibold text-red-700 hover:border-red-400 disabled:opacity-50"
            :disabled="generating"
            @click="retry"
          >
            Réessayer
          </button>
        </div>
      </template>
      <div v-if="generating" class="flex items-center gap-1.5 self-start px-1 text-[11px] text-gray-500">
        <LoaderCircle class="size-3.5 animate-spin" />
        L'IA prépare sa réponse…
      </div>
    </div>

    <div class="mt-2.5 flex flex-wrap items-center gap-2">
      <Input
        v-model="draft"
        :placeholder="entries.length
          ? 'Répondez ou corrigez : « plutôt 12h30 », « inverse les samedis »…'
          : 'Ex. 9h–19h du mardi au vendredi, samedi matin, jamais le mercredi'"
        maxlength="500"
        class="h-9 min-w-52 flex-1 bg-white text-xs"
        :disabled="generating"
        @keydown.enter="send(draft)"
      />
      <BaseButton
        primary
        :loading="generating"
        :disabled="!draft.trim()"
        @click="send(draft)"
      >
        {{ entries.length ? 'Envoyer' : 'Générer le planning' }}
      </BaseButton>
    </div>
    <div v-if="!entries.length" class="mt-2 flex flex-wrap gap-1.5">
      <button
        v-for="example in EXAMPLES"
        :key="example"
        class="h-6 max-w-full truncate rounded-md border border-solid border-primary/20 bg-white px-2 text-[10.5px] text-primary/80 transition-colors hover:border-primary/50"
        :title="example"
        @click="draft = example"
      >
        {{ example }}
      </button>
    </div>
  </div>
</template>
